import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { messagesApi } from '../services/api';
import {
  House,
  Users,
  Buildings,
  BookOpen,
  Chalkboard,
  ClipboardText,
  Question,
  Certificate,
  ChartBar,
  SignOut,
  List,
  UserCircle,
  Exam,
  GearSix,
  EnvelopeSimple,
  FilePdf,
  Crown,
  CaretDown,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gm-class-training/artifacts/1szu61lb_Logo%20G%26M.png";

const adminNavItems = [
  { path: '/admin', icon: House, label: 'Dashboard', end: true },
  { path: '/admin/users', icon: Users, label: 'Usuarios' },
  { path: '/admin/companies', icon: Buildings, label: 'Empresas' },
  { path: '/admin/mandantes', icon: Crown, label: 'Mandantes' },
  { path: '/admin/trainings', icon: Chalkboard, label: 'Capacitaciones' },
  {
    label: 'Preguntas',
    icon: Question,
    children: [
      { path: '/admin/questions', label: 'Preguntas' },
      { path: '/admin/test-bank', label: 'Banco de Preguntas' },
    ],
  },
  { path: '/admin/certificates', icon: Certificate, label: 'Certificados' },
  { path: '/admin/settings', icon: GearSix, label: 'Configuración' },
  { path: '/admin/documents', icon: FilePdf, label: 'Documentos' },
  { path: '/admin/notifications', icon: PaperPlaneTilt, label: 'Notificación Masiva' },
  { path: '/admin/messages', icon: EnvelopeSimple, label: 'G&M Mail', isMail: true },
  { path: '/admin/reports', icon: ChartBar, label: 'Reportes' },
];

const contractorNavItems = [
  { path: '/contractor', icon: House, label: 'Dashboard', end: true },
  { path: '/contractor/workers', icon: Users, label: 'Trabajadores' },
  { path: '/contractor/trainings', icon: Chalkboard, label: 'Capacitaciones' },
  { path: '/contractor/results', icon: ClipboardText, label: 'Resultados' },
  { path: '/contractor/certificates', icon: Certificate, label: 'Certificados' },
  { path: '/contractor/messages', icon: EnvelopeSimple, label: 'G&M Mail', isMail: true },
];

const workerNavItems = [
  { path: '/worker', icon: House, label: 'Dashboard', end: true },
  { path: '/worker/trainings', icon: Chalkboard, label: 'Mis Capacitaciones' },
  { path: '/worker/evaluations', icon: ClipboardText, label: 'Evaluaciones' },
  { path: '/worker/certificates', icon: Certificate, label: 'Mis Certificados' },
];

const clientNavItems = [
  { path: '/client', icon: House, label: 'Dashboard', end: true },
  { path: '/client/trainings', icon: Chalkboard, label: 'Capacitaciones' },
  { path: '/client/reports', icon: ChartBar, label: 'Reportes' },
];

const relatorNavItems = [
  { path: '/relator', icon: House, label: 'Dashboard', end: true },
  { path: '/relator/trainings', icon: Chalkboard, label: 'Mis Capacitaciones' },
];

const NavGroup = ({ item }) => {
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
          childActive
            ? 'text-[#2B4B8C] font-medium'
            : 'text-[#2B4B8C]/70 hover:bg-[#2B4B8C]/5 hover:text-[#2B4B8C]'
        }`}
        data-testid={`nav-group-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <item.icon size={20} weight="duotone" />
        <span className="flex-1 text-left">{item.label}</span>
        <CaretDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 ml-4 pl-3 border-l border-[#2B4B8C]/10 space-y-1">
          {item.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              className={({ isActive }) =>
                `block px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                  isActive
                    ? 'bg-[#2BBAA4]/10 text-[#2BBAA4] font-medium'
                    : 'text-[#2B4B8C]/70 hover:bg-[#2B4B8C]/5 hover:text-[#2B4B8C]'
                }`
              }
              data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ navItems }) => {
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await messagesApi.getUnreadCount();
        setUnreadCount(res.data.count);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getProfilePath = () => {
    const rolePaths = { admin: '/admin/profile', contractor: '/contractor/profile', worker: '/worker/profile', client: '/client/profile', relator: '/relator/profile' };
    return rolePaths[user?.role] || '/admin/profile';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrador',
      contractor: 'Contratista',
      worker: 'Trabajador',
      client: 'Mandante',
      relator: 'Relator'
    };
    return labels[role] || role;
  };

  return (
    <div className={`fixed left-0 ${isImpersonating ? 'top-10' : 'top-0'} ${isImpersonating ? 'h-[calc(100vh-2.5rem)]' : 'h-screen'} w-64 bg-white border-r border-[#2B4B8C]/10 flex flex-col z-50 shadow-sm`}>
      {/* Logo */}
      <div className="p-6 border-b border-[#2B4B8C]/10">
        <img src={LOGO_URL} alt="G&M Class" className="h-14 object-contain" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => (
            item.children ? (
              <NavGroup key={item.label} item={item} />
            ) : (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isActive
                    ? 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-l-2 border-[#2BBAA4] font-medium'
                    : 'text-[#2B4B8C]/70 hover:bg-[#2B4B8C]/5 hover:text-[#2B4B8C]'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon size={20} weight="duotone" />
              <span>{item.label}</span>
              {item.isMail && unreadCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1.5">
                  {unreadCount}
                </span>
              )}
            </NavLink>
            )
          ))}
        </div>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-[#2B4B8C]/10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-[#2B4B8C] hover:text-[#2B4B8C] hover:bg-[#2B4B8C]/5"
              data-testid="user-menu-trigger"
            >
              <div className="w-8 h-8 rounded-full bg-[#2B4B8C] flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-[#2B4B8C]/60">{getRoleLabel(user?.role)}</p>
                {user?.company_name && (
                  <p className="text-xs text-[#2BBAA4] font-medium truncate">{user.company_name}</p>
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white border-[#2B4B8C]/10">
            <DropdownMenuItem 
              onClick={() => navigate(getProfilePath())}
              className="text-[#2B4B8C] focus:text-[#2B4B8C] focus:bg-[#2B4B8C]/5 cursor-pointer"
              data-testid="profile-link"
            >
              <UserCircle className="mr-2" size={16} />
              <span>Mi Perfil</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#2B4B8C]/10" />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              data-testid="logout-btn"
            >
              <SignOut className="mr-2" size={16} />
              <span>Cerrar Sesión</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Mobile Header
export const MobileHeader = ({ onMenuClick }) => {
  const { user } = useAuth();

  return (
    <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#2B4B8C]/10 flex items-center justify-between px-4 z-40 shadow-sm">
      <button 
        onClick={onMenuClick}
        className="p-2 text-[#2B4B8C]/70 hover:text-[#2B4B8C]"
        data-testid="mobile-menu-btn"
      >
        <List size={24} />
      </button>
      <img src={LOGO_URL} alt="G&M Class" className="h-8" />
      <div className="w-8 h-8 rounded-full bg-[#2B4B8C] flex items-center justify-center">
        <span className="text-white text-xs font-semibold">
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </span>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="text-center py-4 text-xs text-[#2B4B8C]/40 border-t border-[#2B4B8C]/10 mt-8">
    &copy;{new Date().getFullYear()} G&M Class
  </footer>
);

// Layout Components
const LayoutShell = ({ navItems }) => {
  const { isImpersonating } = useAuth();
  return (
    <div className={`min-h-screen bg-[#f8fafc] ${isImpersonating ? 'pt-10' : ''}`}>
      <Sidebar navItems={navItems} />
      <main className="md:ml-64 min-h-screen flex flex-col">
        <div className="p-6 md:p-8 flex-1">
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
};

export const AdminLayout = () => <LayoutShell navItems={adminNavItems} />;
export const ContractorLayout = () => <LayoutShell navItems={contractorNavItems} />;
export const WorkerLayout = () => <LayoutShell navItems={workerNavItems} />;
export const ClientLayout = () => <LayoutShell navItems={clientNavItems} />;
export const RelatorLayout = () => <LayoutShell navItems={relatorNavItems} />;
